import { useEffect, useRef } from 'react';

export type CheckboxProps = {
    /**
     * Callback function invoked when the checkbox is selected/unselected.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Whether the checkbox should be checked.
     */
    checked?: boolean;

    /**
     * Whether the checkbox should be round.
     */
    rounded?: boolean;

    /**
     * Whether the checkbox should be indeterminate.
     */
    indeterminate?: boolean;
    /**
     *  Whether the checkbox should be disabled.
     */
    disabled?: boolean;

    /**
     * Label to show beside the checkbox. Clicking the label will also click the checkbox itself.
     */
    label?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

/**
 * Checkbox component
 * @param props
 * @returns
 */
function Checkbox({
    onChange,
    checked = false,
    rounded = false,
    indeterminate = false,
    disabled = false,
    label,
    ...props
}: CheckboxProps): JSX.Element {
    const ref = useRef<HTMLInputElement>();

    useEffect(() => {
        if (checked) {
            ref.current.indeterminate = false;
            ref.current.checked = true;
        } else if (indeterminate) {
            ref.current.indeterminate = true;
            ref.current.checked = false;
        } else {
            ref.current.indeterminate = false;
            ref.current.checked = false;
        }
    }, [checked, indeterminate]);

    return (
        <div className="flex items-center">
            <input
                id={label}
                disabled={disabled}
                type="checkbox"
                className={`disabled:opacity-50 ring-offset-0 focus:ring-indigo-500 h-4 w-4 text-indigo-600 dark:border-gray-600 border-gray-300 dark:bg-white/[0.12] checked:bg-indigo-600 indeterminate:bg-indigo-600 ${
                    rounded ? 'rounded-full' : 'rounded'
                }`}
                ref={ref}
                onChange={(e): void => onChange && onChange(e)}
                {...props}
            />
            {label && (
                <label htmlFor={label} className="pl-3 text-sm text-gray-700 select-none dark:text-gray-200">
                    {label}
                </label>
            )}
        </div>
    );
}
export default Checkbox;
